import * as React from "react";
import {Types}    from "@Common/types";
import {cx}       from "emotion";

import {cs}               from "@Common/react/chain-services";
import {consumeContext}   from "@Common/react/context";
import {Load}             from "@Common/react/load";
import {UseState}         from "@Common/react/use-state";
import {ModalService3}    from "@Common/ui-components/modal-service-3/modal-service-3";
import {Responsive}       from "@Common/ui-components/responsive";
import {SearchableSelect} from "@Common/ui-components/searchable-select/searchable-select";
import {sortMulti}        from "@Common/utils/collections";
import {chain}            from "@Common/utils/fs";
import {equalDeep, omit}  from "@Common/utils/objects";

import {showOfficeEmulation} from "../logic/show-office-emulation";
import {ModalLayout}         from "../modal-layout/modal-layout";

export const AccountSelectionModal = ({
    urlParam,
    onlyOffice = false,
    next: rootNext
}:Types.Xpresso.Common.AccountSelectionModal.AccountSelectionModalProps) => cs(
    ["modal", ({}, next) => ModalService3<() => void>({
        xdQaName: "Account Selection Modal",
        width: 552,
        next: ({params: onSelect, close}) => next({
            onSelect: typeof onSelect === "function" ? onSelect : null,
            close,
        }),
        rootNext,
    })],

    consumeContext("apis"),
    consumeContext("session"),
    consumeContext("userLevels"),
    consumeContext("company"),
    consumeContext("routerHistory"),

    ["multiBranch", ({userLevels}, next) => next(showOfficeEmulation(userLevels))],
    ["isInternalUser", ({userLevels}, next) => next(userLevels?.internalUser)],

    ["forcedBranchId", ({multiBranch, session}, next) => next(multiBranch ? (
        undefined
    ) : (
        session.value.branchId
    ))],

    ["managing", (_, next) => UseState({next})],
    ["state", ({session}, next) => UseState({
        defaultValue: session.value,
        next,
    })],
    ["branchList", ({apis, multiBranch}, next) => Load({
        fetch: () => (multiBranch || onlyOffice)  ? apis.next.getBranchList() : null,
        next,
    })],

    ["agentList", ({state, apis, forcedBranchId}, next) => {
        const branchId = forcedBranchId ?? state.value?.branchId;
        return Load({
            _key: branchId,
            fetch: () => (branchId || !onlyOffice) ? apis.next.getAgentList(branchId) : [],
            next,
        });
    }],
    // @ts-ignore
    ["responsive", (_, next) => <Responsive {...{
        sizes: {
            mobile: 500,
        },
        next,
    }}/>],
    ({modal, managing, state, session, agentList, multiBranch, forcedBranchId, isInternalUser, branchList, responsive, routerHistory}) => ModalLayout({
        title: onlyOffice ? (
            "Office Selection"
        ) : multiBranch ? (
            "Office & User Selection"
        ) : (
            "User Selection"
        ),
        content: (
            <div className={cx("emulation-modal-6ed", {multiBranch})}>
                {responsive.ge("mobile") && <div className="message">
                    {onlyOffice ? (
                        "Please select the office you want to manage"
                    ) : multiBranch ? (
                        "Please select the office and user you want to manage"
                    ) : (
                        "Please select the user you want to manage"
                    )}
                </div>}
                <div className="form">
                    {(multiBranch || onlyOffice) && (
                        <div className="form-group">
                            <div className="control-label">
                                Office
                            </div>

                            {SearchableSelect({
                                getSublabel: (branch) => (<>
                                    <div>
                                        {[
                                            branch.branchAddress1,
                                            branch.branchAddress2,
                                            branch.branchCity,
                                            branch.branchState,
                                            branch.branchZip,
                                        ].filter(Boolean).join(", ")}
                                    </div>
                                    <div
                                        xd-qa-type="dropdown_item"
                                        xd-qa-name={`Office ${branch.idBranch}`}
                                    >
                                        {branch.branchLinkId} {isInternalUser && `| Internal ID: ${branch.idBranch}`}
                                    </div>
                                </>),
                                list: chain(
                                    branchList ?? (session.data.branchName ? [{idBranch: session.value.branchId, branchName: session.data.branchName}] : []),
                                    (_) => sortMulti(_, [
                                        (branch) => branch["branchName"]?.toLowerCase(),
                                    ]),
                                ),
                                loading: !branchList,
                                placeholder: "Please select an office",
                                getLabel: (branch) => chain(
                                    branch.branchName || branch.branchLinkId,
                                    (label) => <span title={label} xd-qa-type="dropdown_item" xd-qa-name={`Office ${label}`}>{label}</span>
                                ),
                                mapSearchAsItemFilter: (search:string) => {
                                    const searchLowercase = search.toLowerCase();
                                    return (branch) => (isInternalUser && branch.idBranch.includes(searchLowercase)) || [
                                        "branchName",
                                        "branchLinkId",
                                        "branchAddress1",
                                        "branchAddress2",
                                        "branchCity",
                                        "branchState",
                                    ].some(field => branch[field].toLowerCase().includes(searchLowercase))
                                },
                                onSelect: async (branch) => {
                                    state.onChange(!branch ? {
                                    } : {
                                        branchId: branch.idBranch,
                                    });
                                },
                                isSelected: (branch) => state.value?.branchId === branch.idBranch,

                                className: "office",
                                searchLabel: "Search an Office",
                                xdQaName: "Office",
                                scrollCloses: false,
                            })}
                        </div>
                    )}
                    {!onlyOffice && <div className="form-group">
                        <div className="control-label">
                            User
                        </div>

                        {SearchableSelect({
                            ...isInternalUser && {
                                getSublabel: (agent) => agent.idAgent && (`Internal ID: ${agent.idAgent}`),
                            },
                            list: [
                                ...chain(
                                    agentList ?? (session.data.agentNameFirst ? [{
                                        idAgent: session.value.agentId,
                                        agentNameFirst: session.data.agentNameFirst,
                                        agentNameLast: session.data.agentNameLast,
                                    }] : []),
                                    (_) => sortMulti(_, [
                                        (agent) => agent["agentNameLast"]?.toLowerCase(),
                                        (agent) => agent["agentNameFirst"]?.toLowerCase(),
                                    ])
                                ),
                            ],
                            loading: !agentList,
                            disabled: multiBranch && !state.value?.branchId,
                            getLabel: (agent) => agent === "branch_order" ? (
                                "Branch Order"
                            ) : agent.agentLinkId ? (
                                agent.agentNameLast + ", " + agent.agentNameFirst + ` (${agent.agentLinkId})`
                            ) : (
                                agent.agentNameLast + ", " + agent.agentNameFirst
                            ),
                            placeholder: "Please select a User",
                            onSelect: async (agent) => {
                                if (!agent) {
                                    state.change((s) => omit(s, ["agentId", "isBranchOrder"]));
                                } else if (agent === "branch_order") {
                                    state.change((s) => ({
                                        ...omit(s, ["agentId"]),
                                        isBranchOrder: true,
                                    }))
                                } else {
                                    state.change((s) => ({
                                        ...omit(s, ["isBranchOrder"]),
                                        agentId: agent.idAgent,
                                    }))
                                }
                            },
                            isSelected: (agent) => agent === "branch_order" ? (
                                state.value?.isBranchOrder
                            ) : (
                                state.value?.agentId === agent.idAgent
                            ),

                            searchLabel: "Search a User",
                            xdQaName: "User",
                            scrollCloses: false,
                        })}
                    </div>}
                </div>
                {responsive.lt("mobile") && <div className="message">
                    {multiBranch ? (
                        "Please select the office and user you want to manage"
                    ) : (
                        "Please select the user you want to manage"
                    )}
                </div>}

                {!equalDeep(
                    {
                        ...state.value,
                        ...forcedBranchId && {branchId: forcedBranchId},
                    },
                    session.value
                )}
            </div>
        ),
        buttons: [
            {
                label: "Clear Selection",
                disabled: (forcedBranchId || !state.value?.branchId) && (!state.value?.agentId && !state.value?.isBranchOrder),
                onClick: () => state.onChange({}),
            },

            (() => {

                const newEmulation = {
                    ...state.value,
                    ...forcedBranchId && {branchId: forcedBranchId},
                };

                return {
                    label: managing.value ? (
                        managing.value.agentId ? (
                            "managing Agent..."
                        ) : managing.value.isBranchOrder ? (
                            "managing Branch Order..."
                        ) : managing.value.branchId ? (
                            "managing Branch..."
                        ) : (
                            "Clearing..."
                        )
                    ) : (
                        "Confirm Selection"
                    ),
                    primary: true,
                    disabled: (
                        (equalDeep(newEmulation, session.value) && !modal.onSelect) ||
                        managing.value
                    ),
                    onClick: async () => {
                        onlyOffice ? (
                            routerHistory.replace(`${urlParam}?office=${encodeURIComponent(state.value.branchId)}`)
                        ) : multiBranch ? (
                            routerHistory.replace(`${urlParam}?office=${encodeURIComponent(state.value.branchId)}&agent=${encodeURIComponent(state.value.agentId)}`)
                        ) : (
                            routerHistory.replace(`${urlParam}?office=${encodeURIComponent(state.value.agentId)}`)
                        )

                        modal.close();
                    },
                };
            })(),
        ],
    }),
);
